<template>
    <div>
        <b-container v-if="started">
            <b-row>
                <b-col
                    cols="8"
                    class="preview-area"
                    ref="printElementsPreviewArea"
                >
                    <div ref="printElements">
                        <print-map
                            v-if="
                                userSettings.printselection !== 'results' &&
                                previewConfig
                            "
                            ref="printMap"
                            :previewConfig="previewConfig"
                            :dpi="dpi"
                            :mode="mode"
                            :userSettings="userSettings"
                            :originalMap="map"
                            :logo="logoData"
                            :northarrow="bitmaps.northarrow"
                            @loading="$_onMapLoading($event)"
                        />
                        <print-extras
                            v-if="
                                started &&
                                userSettings.printselection === 'results'
                            "
                            ref="printExtras"
                            :previewConfig="previewConfig"
                            :mode="mode"
                            :userSettings="userSettings"
                        />
                    </div>
                </b-col>
                <b-col cols="4">
                    <print-settings
                        v-if="paperSettings"
                        :paperSize="paperSize"
                        :dpi="dpi"
                        :paperSettings="paperSettings"
                        :orgScale="orgScale"
                        :currentScale="currentScale"
                        :defaultLogoId="bitmaps.logoId"
                        :logoOptions="
                            $_availableLogos(this.organizationBitmaps)
                        "
                        @print="$_onPrint"
                        @preview="$refs.printMap.$_updateMapResolution()"
                        @settings="$_onChangeSettings($event)"
                        @cancel="$emit('cancel')"
                    ></print-settings>
                </b-col>
            </b-row>
        </b-container>
    </div>
</template>

<script>
    import { mapState, mapActions, mapGetters } from 'vuex';
    import { normalNameToPrintSettingsCheckboxName } from '../../services/utilities/utilities';
    import { availableLogos } from '../../services/utilities';
    import { getScaleFromResolution } from '../../services/map';
    import { DEFAULTRES } from '../../constants';
    import {
        createCss,
        paperSettings
    } from '../../services/printing/printSettings';
    import PrintSettings from './PrintSettings.vue';
    import PrintMap from './PrintMap.vue';
    import PrintExtras from './PrintExtras.vue';

    export default {
        name: 'PrintView',
        components: {
            'print-settings': PrintSettings,
            'print-map': PrintMap,
            'print-extras': PrintExtras
        },
        props: { map: { type: Object, required: true } },
        data() {
            return {
                loadingState: {
                    tiles: null,
                    message: null,
                    stage: 0,
                    errors: []
                },
                paperSize: 'A4 - staand',
                orgScale: null,
                currentScale: null,
                dpi: DEFAULTRES,
                userSettings: {},
                mode: 'preview',
                started: false,
                readyToPrint: null,
                failedReadyToPrint: null,
                useCompression: true,
                bitmaps: null,
                organizationBitmaps: []
            };
        },
        created() {
            this.orgScale = this.originalScale;
            this.currentScale = this.originalScale;
        },
        methods: {
            async $_onStart() {
                await this.$store.dispatch('getPrintParameters');
                await this.$_getBitmaps(this.userIsAuthenticated);
                if (this.userIsAuthenticated) {
                    await this.$_getOrganizationBitmaps();
                }
                this.started = true;
            },

            $_onMapLoading(isLoading) {
                this.loadingState.message = this.$t('printComponent.loadingTiles');
                this.loadingState.tiles = isLoading;
            },
            $_onChangeSettings(userSettings) {
                const requiredItems = Object.entries(
                    this.paperSettings.settings.components
                )
                    // select the options and transform the name to use as toggle value
                    .map((item) => ({
                        name: normalNameToPrintSettingsCheckboxName(item[0]),
                        ...item[1].options
                    }))
                    // the not optional ones are allways shown and need to be in this araay
                    .filter(
                        ({ required, optional }) =>
                            required === true && optional === false
                    )
                    // we only use the name and value true now
                    .map(({ name }) => ({ [name]: true }))
                    // create an object from this array
                    .reduce((a, b) => Object.assign(a, b), {});
                // combine the usersettings with the items shown by default for this template
                this.userSettings = { ...requiredItems, ...userSettings };
                // force the scale to be an integer
                if (typeof this.userSettings.scale === 'string') {
                    this.userSettings.scale = parseInt(this.userSettings.scale, 10);
                }
                this.currentScale = this.userSettings.scale;
                this.paperSize = userSettings.size;
                this.dpi = DEFAULTRES;
            },
            async $_getOrganizationBitmaps() {
                const organizationId = this.getOrganizationId;
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetOrganizationLogos',
                    parameters: {
                        id: organizationId
                    }
                });
                if (result?.data) {
                    this.organizationBitmaps = Object.assign([], result.data);
                }
            },
            $_availableLogos(bitmaps) {
                if (bitmaps) {
                    return availableLogos(bitmaps);
                } else {
                    return [];
                }
            },
            async $_onPrint() {
                const printJob = {
                    map: this.map,
                    userSettings: this.userSettings,
                    dpi: this.dpi,
                    previewConfig: this.previewConfig,
                    logo: this.logoData,
                    northarrow: this.bitmaps.northarrow
                };
                this.$store.commit('SET_PRINT_JOB', printJob);
                this.$emit('cancel');
                this.$router.push({ name: 'ngPrint' });
            },
            async $_getBitmaps(userIsAuthenticated) {
                let bitmaps = null;
                if (!userIsAuthenticated) {
                    bitmaps = await this.$store.dispatch('useApiData', {
                        name: 'WebGetWebThemeLogoAndNorthArrow',
                        parameters: { id: this.$store.getters.getWebTheme.id }
                    });
                } else {
                    bitmaps = await this.$store.dispatch('useApiData', {
                        name: 'GetOrganizationLogoAndNorthArrow'
                    });
                }
                this.bitmaps = bitmaps ? bitmaps.data : null;
            }
        },
        watch: {
            'loadingState.tiles': function (newVal) {
                const printTilesHaveLoaded = !newVal && this.mode === 'print';
                if (printTilesHaveLoaded) {
                    this.readyToPrint && this.readyToPrint();
                }
            },
            mode: function () {
                this.loadingState.tiles = false;
                this.readyToPrint = null;
            }
        },
        computed: {
            ...mapState(['printSettings']),
            ...mapActions(['getBitmaps']),
            ...mapGetters(['getOrganizationId']),
            userIsAuthenticated: function () {
                return (
                    this.$store.getters.userIsAuthenticated ||
                    this.$store.state.requiresAuthentication
                );
            },
            paperSettings: function () {
                if (
                    !(
                        this.printSettings &&
                        this.printSettings.templates &&
                        this.printSettings.templates.length > 0
                    )
                ) {
                    return undefined;
                }
                if (!this.paperSize) {
                    return undefined;
                }
                return paperSettings(this.printSettings, this.paperSize);
            },
            previewConfig: function () {
                return createCss(this.paperSettings, this.dpi);
            },
            printConfig: function () {
                if (!this.paperSettings) {
                    return undefined;
                }
                const width = this.paperSettings.width;
                const height = this.paperSettings.height;
                return {
                    paper: {
                        width: width,
                        height: height
                    }
                };
            },
            originalScale: function () {
                return getScaleFromResolution(
                    this.map,
                    this.map.getView().getResolution()
                );
            },
            logoData: function () {
                if (this.userIsAuthenticated) {
                    const logoId = this.userSettings?.logo
                        ? this.userSettings?.logo
                        : this.bitmaps.logoId;
                    const logo = this.organizationBitmaps.find(
                        (o) => o.id == logoId
                    );
                    return Buffer.from(logo.imageData, 'base64').toString();
                } else {
                    return this.bitmaps.logo;
                }
            }
        }
    };
</script>

<style scoped lang="scss">
.preview-area {
    overflow: hidden;
}
</style>
